import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Search, 
  Users, 
  MessageSquare, 
  Briefcase, 
  GraduationCap,
  UserCircle,
  Building,
  Calendar,
  Loader2,
  Filter,
  SortAsc,
  Grid,
  List,
  ArrowRight,
  X,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import Profilecard from '../Profilecard';
import DisplayPost from '../DisplayPost';
import JobPost from '../JobPost';

export const SearchedResults = () => {
  const location = useLocation();
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  
  const searchParams = new URLSearchParams(location.search);
  const searchKeyword = searchParams.get('search');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchKeyword = searchParams.get('search');

    const fetchSearchResults = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/search/search?keyword=${searchKeyword}`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setSearchResults(data);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setSearchResults(null);
      } finally {
        setLoading(false);
      }
    };

    if (searchKeyword) {
      fetchSearchResults();
    }
  }, [location.search]);

  // Calculate total results
  const getTotalResults = () => {
    if (!searchResults) return 0;
    return Object.values(searchResults).reduce((total, category) => {
      return total + (Array.isArray(category) ? category.length : 0);
    }, 0);
  };

  // Filter categories
  const getFilteredCategories = () => {
    if (!searchResults) return [];
    
    const categories = [
      { key: 'alumni', label: 'Members', icon: Users, count: searchResults.alumni?.length || 0 },
      { key: 'forum', label: 'Forums', icon: MessageSquare, count: searchResults.forum?.length || 0 },
      { key: 'group', label: 'Groups', icon: Building, count: searchResults.group?.length || 0 },
      { key: 'job', label: 'Jobs', icon: Briefcase, count: searchResults.job?.length || 0 },
      { key: 'internship', label: 'Internships', icon: GraduationCap, count: searchResults.internship?.length || 0 }
    ];

    return categories.filter(cat => cat.count > 0);
  };

  const filteredCategories = getFilteredCategories();

  const clearFilters = () => {
    setActiveFilter('all');
    setSortBy('relevance');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 dynamic-site-bg rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 text-white animate-spin" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Searching...</h3>
          <p className="text-gray-600 text-sm sm:text-base">Finding the best results for "{searchKeyword}"</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 dynamic-site-bg rounded-lg flex items-center justify-center">
              <Search size={16} className="sm:size-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Search Results</h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {getTotalResults()} results found for "{searchKeyword}"
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-3 sm:p-4 mb-4">
          {/* Search Term Display */}
          <div className="mb-3 sm:mb-4">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <Search size={14} className="text-gray-400" />
              <span className="text-sm text-gray-700">Searching for:</span>
              <span className="text-sm font-medium text-gray-900">"{searchKeyword}"</span>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 dynamic-site-bg text-white rounded-lg hover:opacity-90 transition-opacity duration-200 text-sm"
              >
                <Filter size={14} />
                <span>Categories</span>
                {filteredCategories.length > 0 && (
                  <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                    {filteredCategories.length}
                  </span>
                )}
                <ChevronDown size={14} className={`transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              
              {activeFilter !== 'all' && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-2 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 text-sm"
                >
                  <RefreshCw size={12} />
                  <span className="hidden sm:inline">Clear</span>
                </button>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid size={14} />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List size={14} />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>

          {/* Filter Options */}
          {showFilters && filteredCategories.length > 0 && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-3 py-1.5 rounded-lg font-medium transition-all duration-200 text-xs ${
                    activeFilter === 'all'
                      ? 'dynamic-site-bg text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All ({getTotalResults()})
                </button>
                {filteredCategories.map((category) => (
                  <button
                    key={category.key}
                    onClick={() => setActiveFilter(category.key)}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 text-xs ${
                      activeFilter === category.key
                        ? 'dynamic-site-bg text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <category.icon size={12} />
                    {category.label} ({category.count})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Content */}
        {searchResults && filteredCategories.length > 0 ? (
          <div className="space-y-6">
            {/* Members Section */}
            {(activeFilter === 'all' || activeFilter === 'alumni') && searchResults.alumni && searchResults.alumni.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="dynamic-site-bg p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Users size={20} className="text-white" />
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                      Members ({searchResults.alumni.length})
                    </h2>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
                      {searchResults.alumni.map((item) => (
                        <div key={item._id} className="transform hover:scale-105 transition-transform duration-200">
                          <Profilecard member={item} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.alumni.map((item) => (
                        <div key={item._id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                          <Profilecard member={item} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Groups Section */}
            {(activeFilter === 'all' || activeFilter === 'group') && searchResults.group && searchResults.group.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="dynamic-site-bg-3 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Building size={20} className="text-white" />
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                      Groups ({searchResults.group.length})
                    </h2>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <DisplayPost groups={searchResults.group} />
                </div>
              </div>
            )}

            {/* Jobs Section */}
            {(activeFilter === 'all' || activeFilter === 'job') && searchResults.job && searchResults.job.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-600 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Briefcase size={20} className="text-white" />
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                      Jobs ({searchResults.job.length})
                    </h2>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {searchResults.job.map((item) => (
                      <div key={item._id} className="transform hover:scale-105 transition-transform duration-200">
                        <JobPost
                          userId={item.userId}
                          id={item._id}
                          jobTitle={item.title}
                          company={item.company}
                          description={item.description}
                          salaryMin={item.salaryMin}
                          salaryMax={item.salaryMax}
                          picture={item.picture}
                          duration={item.duration}
                          jobType={item.jobType}
                          questions={item.questions}
                          category={item.category}
                          currency={item.currency}
                          type={item.type}
                          createdAt={item.createdAt}
                          attachments={item.attachments}
                          title='Jobs'
                          titleS='job'
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Internships Section */}
            {(activeFilter === 'all' || activeFilter === 'internship') && searchResults.internship && searchResults.internship.length > 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <GraduationCap size={20} className="text-white" />
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                      Internships ({searchResults.internship.length})
                    </h2>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {searchResults.internship.map((item) => (
                      <div key={item._id} className="transform hover:scale-105 transition-transform duration-200">
                        <JobPost
                          userId={item.userId}
                          id={item._id}
                          jobTitle={item.title}
                          description={item.description}
                          salaryMin={item.salaryMin}
                          salaryMax={item.salaryMax}
                          picture={item.picture}
                          duration={item.duration}
                          jobType={item.internshipType}
                          questions={item.questions}
                          category={item.category}
                          currency={item.currency}
                          createdAt={item.createdAt}
                          type={item.type}
                          attachments={item.attachments}
                          title='Internships'
                          titleS='internship'
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* No Results State */
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 p-6 sm:p-12 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Search className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              No results found
            </h3>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
              We couldn't find any results for "{searchKeyword}". Try adjusting your search terms or browse by category.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <span className="text-xs sm:text-sm text-gray-500 w-full sm:w-auto mb-2 sm:mb-0">Try searching for:</span>
              {['Alumni', 'Jobs', 'Groups', 'Events', 'Forums'].map((suggestion) => (
                <button
                  key={suggestion}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs sm:text-sm transition-colors duration-200"
                  onClick={() => {
                    window.location.href = `/home/?search=${suggestion}`;
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Back to Top Button */}
        {filteredCategories.length > 0 && (
          <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-10 h-10 sm:w-12 sm:h-12 dynamic-site-bg text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 flex items-center justify-center"
            >
              <ArrowRight size={16} className="sm:size-5 rotate-[-90deg]" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchedResults;
